import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin chính sách bảo mật
 */
export interface PrivacyPolicyItem {
  /** ID duy nhất của chính sách */
  id: number;
  /** Tiêu đề chính sách */
  title: string;
  /** Nội dung chính sách */
  content: string;
  /** Phiên bản chính sách */
  version: string;
  /** Đường dẫn file (nếu có) */
  filePath?: string;
  /** URL truy cập file (nếu có) */
  fileUrl?: string;
  /** Tên file gốc (nếu có) */
  originalFileName?: string;
  /** Trạng thái hoạt động của chính sách */
  isActive: boolean;
  /** Thời gian xuất bản (tùy chọn) */
  publishedAt?: string;
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật cuối */
  updatedAt: string;
}

/**
 * Interface cho yêu cầu tạo chính sách mới
 */
export interface CreatePrivacyPolicyRequest {
  /** Tiêu đề chính sách */
  title: string;
  /** Nội dung chính sách */
  content?: string;
  /** Phiên bản chính sách */
  version: string;
  /** Trạng thái hoạt động (tùy chọn, mặc định false) */
  isActive?: boolean;
}

/**
 * Interface cho yêu cầu cập nhật chính sách
 */
export interface UpdatePrivacyPolicyRequest {
  /** Tiêu đề chính sách (tùy chọn) */
  title?: string;
  /** Nội dung chính sách (tùy chọn) */
  content?: string;
  /** Phiên bản chính sách (tùy chọn) */
  version?: string;
  /** Trạng thái hoạt động (tùy chọn) */
  isActive?: boolean;
}

/**
 * Service xử lý các chức năng quản lý chính sách bảo mật
 * Bao gồm xem, tạo, cập nhật, xóa chính sách
 */
export class PrivacyPolicyService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /** Lấy base URL (hữu ích để dựng file URL khi backend chưa trả sẵn) */
  static getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  // ================== PUBLIC PRIVACY POLICY ==================
  
  /**
   * Lấy chính sách đang hoạt động (public)
   * @returns Promise<PrivacyPolicyItem | null> - Thông tin chính sách đang hoạt động hoặc null nếu chưa có
   * @throws Error - Khi không thể tải chính sách
   */
  static async getActivePrivacyPolicy(): Promise<PrivacyPolicyItem | null> {
    const response = await fetch(`${this.baseUrl}/privacy-policies/active`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      // 404 khi chưa có privacy policy active → trả null để UI hiển thị fallback
      if (response.status === 404) return null;
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải chính sách bảo mật.';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    return response.json();
  }

  // ================== ADMIN PRIVACY POLICY ==================
  
  /**
   * Lấy danh sách tất cả chính sách (admin)
   * @returns Promise<PrivacyPolicyItem[]> - Danh sách tất cả chính sách
   * @throws Error - Khi không thể tải danh sách chính sách
   */
  static async listPrivacyPolicies(): Promise<PrivacyPolicyItem[]> {
    const response = await apiClient.get('/privacy-policies');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách chính sách bảo mật';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Tạo chính sách mới (admin)
   * @param data - Thông tin chính sách cần tạo
   * @returns Promise<PrivacyPolicyItem> - Thông tin chính sách vừa tạo
   * @throws Error - Khi tạo chính sách thất bại
   */
  static async createPrivacyPolicy(data: CreatePrivacyPolicyRequest): Promise<PrivacyPolicyItem> {
    const response = await apiClient.post('/privacy-policies', data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Tạo chính sách bảo mật thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Cập nhật thông tin chính sách (admin)
   * @param id - ID của chính sách cần cập nhật
   * @param data - Thông tin chính sách cần cập nhật
   * @returns Promise<PrivacyPolicyItem> - Thông tin chính sách sau khi cập nhật
   * @throws Error - Khi cập nhật chính sách thất bại
   */
  static async updatePrivacyPolicy(id: number, data: UpdatePrivacyPolicyRequest): Promise<PrivacyPolicyItem> {
    const response = await apiClient.patch(`/privacy-policies/${id}`, data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật chính sách bảo mật thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xóa chính sách (admin)
   * @param id - ID của chính sách cần xóa
   * @returns Promise<void>
   * @throws Error - Khi xóa chính sách thất bại
   */
  static async deletePrivacyPolicy(id: number): Promise<void> {
    const response = await apiClient.delete(`/privacy-policies/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xóa chính sách bảo mật thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Upload tệp đính kèm cho chính sách (admin)
   * @param id - ID chính sách
   * @param file - Tệp cần upload
   */
  static async uploadPrivacyPolicyFile(id: number, file: File): Promise<PrivacyPolicyItem> {
    const form = new FormData();
    form.append('file', file);
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await apiClient.request(`/privacy-policies/${id}/file`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Upload tệp thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }
}
